/**
 * Administering a principal's authority — the facets an Ops binding holds (ADR-0060).
 *
 * ## Why the vocabulary lives here
 *
 * Three things state it: this array, the `OpsFacet` union in `apps/api`, and
 * `principal_bindings_facets_known` in migration 0029. That is one too many, so the union is
 * **derived** from this array rather than written out again — `apps/api/src/common/principal.ts`
 * imports it, and a member added to one and not the other is a compile error rather than a
 * route that silently never matches.
 *
 * The database stays the enforcing side, as it does for `fee_codes`: a CHECK refuses a value
 * nobody named even when the write comes from a psql prompt. This array is what lets a
 * *request* be refused with a field path instead of a constraint name.
 *
 * Each facet is defined in migration 0029's header. The one worth repeating anywhere it is
 * read: **`hub_finance` counts cash that has arrived at a Hub; `finance` decides the rules
 * money moves under.** Same word, two jobs, neither implying the other.
 */
import { z } from 'zod';
import { renderedText } from './rendered-text.js';
/**
 * The closed set. 02a Assumption 1's four, then the area facets the configuration console divides
 * by — the seven of migration 0029 and `notifications` (migration 0067, ADR-0078), which decides
 * what the platform tells whom — then `access` (migration 0045, ADR-0067). The order is that
 * grouping rather than alphabetical, because the grouping is the thing a reader needs.
 *
 * `access` administers people: who exists in Ops and which of the *other* facets each holds.
 * It never reaches itself or `platform_staff` — only platform staff grant those — and that
 * ceiling is what answers ADR-0060 §2's objection to a facet that grants facets.
 */
export const OPS_FACETS = [
    'compliance',
    'adjudicator',
    'hub_staff',
    'hub_finance',
    'pricing',
    'finance',
    'policy',
    'geography',
    'catalogue',
    'fleet',
    'directory',
    'notifications',
    'access',
];
/**
 * Replace the whole set, rather than adding or removing one.
 *
 * A set is what the column holds, and a partial verb invites two calls that disagree about
 * what the operator meant — `add: ['pricing']` sent twice is indistinguishable from a set that
 * was already correct. Sending the whole array means the request says what the answer should
 * be, and the response can say what moved.
 *
 * `.max(OPS_FACETS.length)` is not a security bound; it is what stops a caller sending the
 * same facet ten thousand times and making the event payload the largest row in the table.
 * Duplicates are refused outright for the same reason the array is a set.
 */
/** A set of facets: each named once, none outside the vocabulary. Shared by every writer. */
const facetSetSchema = z
    .array(z.enum(OPS_FACETS))
    .max(OPS_FACETS.length)
    .refine((facets) => new Set(facets).size === facets.length, {
    message: 'a facet may appear once',
});
export const amendFacetsSchema = z
    .object({
    facets: facetSetSchema,
    /**
     * Why. Free text, required, and kept in the payload rather than in the reason code.
     *
     * The same split `merchant.amended` uses: the reason **code** is derived from what
     * actually changed, so `SELECT … WHERE reason_code = 'facets_granted'` is answerable,
     * while the sentence a person typed is the thing an incident review reads.
     */
    note: z.string().trim().min(1).max(500),
})
    .strict();
export const amendFacetsRendered = renderedText(amendFacetsSchema, {
    exempt: { note: 'stored only in the principal.facets_amended event payload; no view renders it' },
});
// ---------------------------------------------------------------------------
// People — the Ops colleagues `access` administers (ADR-0067, A2)
// ---------------------------------------------------------------------------
/**
 * Where a person stands, as the people console reads it off a binding.
 *
 * `pending` is an invitation nobody has accepted yet; `withdrawn` is one that was taken back
 * before anyone did; `revoked` is a person who signed in and then lost access. The last two are
 * the same `status = 'revoked'` on the row and are kept apart here, because "we took back an
 * invitation" and "we removed a colleague" are different facts to the person reading the list.
 *
 * Since migration 0048 the person an invitation names answers it: `declined` is a no, and
 * `accepted` is a yes from somebody who already had an account — the invitation's facets joined
 * that account, and this row is the record of it. A yes from a new person makes the row `active`.
 */
export const PERSON_STATES = ['pending', 'active', 'withdrawn', 'revoked', 'accepted', 'declined'];
/**
 * Why a person's access is revoked — a closed enum, for the reason `REVOCATION_REASONS` gives:
 * free text in an append-only store can be neither corrected nor counted.
 *
 * Its own list rather than the handset one, because `device_lost` and `enrolment_error` are not
 * things that happen to a colleague and a select offering them would invite the wrong code.
 */
export const PERSON_REVOCATION_REASONS = [
    'staff_departed',
    'access_no_longer_needed',
    'compromise_suspected',
];
/** A required operator sentence, kept in the event payload — `amendFacetsSchema.note`'s rule. */
const noteSchema = z.string().trim().min(1).max(500);
/**
 * Invite a colleague: a pending `bootstrap_email` row the person it names accepts or declines
 * when they sign in with that address (migration 0048).
 *
 * `label` is how this screen shows the invitation until then and the person after — required,
 * because the address is never returned (the console reads no email back) and a row with neither
 * would be unrecognisable. It is operator-written text for an Ops screen (`principal.ts`), and it
 * is **not** written into any event.
 *
 * No `platformStaff` here: that is granted separately, by platform staff alone.
 */
export const invitePersonSchema = z
    .object({
    email: z.string().trim().toLowerCase().email().max(254),
    label: z.string().trim().min(1).max(120),
    facets: facetSetSchema,
    note: noteSchema,
})
    .strict();
export const invitePersonRendered = renderedText(invitePersonSchema, {
    // The invitation response and the people list both show the label.
    prose: ['label'],
    exempt: {
        email: 'the address the first sign-in claims: stored on the binding and never returned (PersonView has none)',
        note: 'stored only in the principal.invited event payload; no view renders it',
    },
});
export const withdrawInvitationSchema = z.object({ note: noteSchema }).strict();
export const withdrawInvitationRendered = renderedText(withdrawInvitationSchema, {
    exempt: { note: 'stored only in the principal.invitation_withdrawn event payload; no view renders it' },
});
export const revokePersonSchema = z.object({ reasonCode: z.enum(PERSON_REVOCATION_REASONS) }).strict();
/** A closed reason code: no free text. */
export const revokePersonRendered = renderedText(revokePersonSchema, {});
/** Platform staff only (ADR-0067 §2): the one authority `access` never reaches. */
export const setPlatformStaffSchema = z.object({ platformStaff: z.boolean(), note: noteSchema }).strict();
export const setPlatformStaffRendered = renderedText(setPlatformStaffSchema, {
    exempt: { note: 'stored only in the principal.platform_staff_changed event payload; no view renders it' },
});
/**
 * Detail codes a people refusal can arrive as, beside the error code. Exported so the console's
 * i18n gate reads this list rather than a copy of it — a code with no copy is shipped as a bare
 * identifier into an Arabic-first screen (M14-2's rule: not shipped until something renders it).
 */
export const PEOPLE_ISSUE_CODES = [
    'already_invited',
    'not_a_person',
    'not_pending',
    'not_claimed',
    'revoked',
    'unchanged',
    'self',
    'access',
    'platform_staff',
    'phone_shaped',
    'direction_override',
];
/**
 * Why answering an invitation was refused (ADR-0067 A3): one the answering account wrote itself
 * (`self`), one no longer waiting for it (`not_pending`), or an account that cannot hold what an
 * invitation grants (`not_a_person`). The landing page's card renders each from `invitation.issue.*`.
 */
export const INVITATION_ISSUE_CODES = ['self', 'not_pending', 'not_a_person'];
// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------
/**
 * Amending one service class — the SLA budget it promises, and who owns that number.
 *
 * **`budgetSeconds` is `int().nullable()` and deliberately not `.positive()`.** The rule that
 * zero is unlawful lives in `budgetSecondsProblem` in `@tard/db`, which `pnpm load:reference`
 * has applied since it was written, and a second statement of it here could disagree with the
 * first. **One rule, not one message** — corrected in M10: the loader prints the sentence, while
 * the route logs it and answers with the detail code `unruled_budget`, because `ApiProblem`'s
 * note never reaches a client (§4). This docblock and ADR-0060 §5 both said the screen read the
 * sentence; it never did (ADR-0061).
 *
 * **Null is not zero, and it is the reason the field is nullable at all.** `cross_cluster`
 * carries null on purpose: §5 forbids holding such an Order to the 24-hour promise, and no
 * number is better than a borrowed one.
 *
 * `owner` and `reviewDate` are required, not defaulted. Both columns are NOT NULL with no
 * default, and both loaders refuse to default them for §7's reason — a route that defaulted
 * would be the first writer in this platform able to produce a row owned by nobody.
 */
export const amendServiceClassSchema = z
    .object({
    budgetSeconds: z.number().int().nullable(),
    /**
     * The class's own sub-budgets and return promise, in seconds (P2-3). Each nullable and not
     * refined, for `budgetSeconds`' reason: `clockBudgetsProblem` in `@tard/db` holds the rule —
     * positive whole seconds, and the two sub-budgets strictly inside the promise — and the
     * table states the sum again as a CHECK. Null means that leg, or the return, is not measured.
     */
    subBudgetASeconds: z.number().int().nullable(),
    subBudgetBSeconds: z.number().int().nullable(),
    returnBudgetSeconds: z.number().int().nullable(),
    /**
     * The working window, in the dropoff City Cluster's own timezone (ADR-0065, M15-3).
     *
     * **Nullable and not `.refine()`d against each other**, for `budgetSeconds`' reason exactly:
     * the rule that a cutoff follows an opening, and that neither stands alone, lives in
     * `workingWindowProblem` in `@tard/db`, which `pnpm load:reference` applies to a file. A
     * second statement here could disagree with the first, and the disagreement would be silent
     * — a window the screen accepted and the loader refuses defers nothing while reading as set.
     *
     * `HH:MM` and `HH:MM:SS` are both admitted because `<input type="time">` sends the short
     * form and Postgres stores the long one; the service compares in the stored spelling.
     */
    opensLocalTime: z
        .string()
        .regex(/^([01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/u, 'local_time_format')
        .nullable(),
    cutoffLocalTime: z
        .string()
        .regex(/^([01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/u, 'local_time_format')
        .nullable(),
    /**
     * The ISO weekdays the class works, 1 = Monday … 7 = Sunday, or `null` for every day (P2-4). A
     * day outside them is a rest day, deferred past as a holiday is.
     *
     * Whole numbers and nothing more, for the window's reason: `workingWeekdaysProblem` in
     * `@tard/db` holds the rule — each day once, in range, at least one, and only with a window —
     * and names the refusal, which a `.min()` here would have answered first as a bare `too_small`.
     */
    workingWeekdays: z.array(z.number().int()).nullable(),
    owner: z.string().trim().min(1).max(200),
    reviewDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/u, 'review_date_format'),
    note: z.string().trim().min(1).max(500),
})
    .strict();
const LOCAL_TIME_EXEMPT = 'HH:MM or HH:MM:SS (local_time_format), which holds no phone-shaped digit run';
export const amendServiceClassRendered = renderedText(amendServiceClassSchema, {
    prose: ['owner'],
    exempt: {
        opensLocalTime: LOCAL_TIME_EXEMPT,
        cutoffLocalTime: LOCAL_TIME_EXEMPT,
        reviewDate: 'a YYYY-MM-DD date (review_date_format), which holds no phone-shaped digit run',
        note: 'stored only in the config.service_classes.amended event payload; no view renders it',
    },
});
//# sourceMappingURL=principals.js.map